import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer } from "recharts";
import { ArrowLeft, Info, ChevronDown, Flag, CheckCircle2, XCircle } from "lucide-react";
import { api, apiError, resolveAsset } from "../lib/api";
import { FeedbackLink } from "../components/exam/FeedbackLink";

const BAND_STYLES = {
  "Above Target": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Target": "bg-blue-50 text-blue-700 border-blue-200",
  "Below Target": "bg-amber-50 text-amber-700 border-amber-200",
  "Needs Improvement": "bg-rose-50 text-rose-700 border-rose-200",
};

export default function ReportPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [domain, setDomain] = useState("all");
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get(`/exam-sessions/${sessionId}/report`)
      .then(({ data }) => setData(data))
      .catch((e) => {
        toast.error(apiError(e, "Could not load report"));
        navigate("/", { replace: true });
      });
  }, [sessionId, navigate]);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.questions.filter((q) => {
      if (filter === "wrong" && q.is_correct) return false;
      if (filter === "marked" && !q.marked) return false;
      if (domain !== "all" && q.domain !== domain) return false;
      return true;
    });
  }, [data, filter, domain]);

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-400 text-sm font-medium tracking-wide">Scoring…</div>
      </div>
    );
  }

  const { report, trend } = data;
  const domains = data.report.domains.map((d) => d.domain);
  const trendData = trend.map((t, i) => ({ name: `#${i + 1}`, pct: t.pct }));

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button data-testid="back-to-dashboard-btn" onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <div className="text-sm text-slate-500">
            Exam {data.exam_number} · {data.mode === "testing" ? "Testing Mode" : "Study Mode"}
            {data.auto_submitted && <span className="ml-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Auto-submitted</span>}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2">Score Report</p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div data-testid="overall-score" className="font-mono text-6xl font-semibold text-slate-900 tracking-tight">
              {report.overall.pct}<span className="text-3xl text-slate-400">%</span>
            </div>
            <div className="mt-2 text-sm text-slate-500">{report.overall.correct} of {report.overall.total} correct</div>
            <span className={`mt-3 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${BAND_STYLES[report.overall.band]}`}>
              {report.overall.band}
            </span>
            <p className="mt-3 text-[11px] text-slate-400">
              Completed {new Date(data.completed_at).toLocaleString()}
            </p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <div className="flex items-center gap-1.5 mb-4">
              <h3 className="text-sm font-semibold text-slate-800">Domain performance</h3>
              <span className="group relative cursor-help">
                <Info className="w-3.5 h-3.5 text-slate-400" />
                <span className="invisible group-hover:visible absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 bg-slate-900 text-white text-[11px] leading-snug rounded-lg px-3 py-2 z-10">
                  {data.disclaimer}
                </span>
              </span>
            </div>
            <div className="space-y-3">
              {report.domains.map((d) => (
                <div key={d.domain} data-testid={`domain-row-${d.domain.replace(/\s/g, "-").toLowerCase()}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{d.domain}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${BAND_STYLES[d.band]}`}>{d.band}</span>
                  </div>
                  <div className="mt-1.5 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 rounded-full" style={{ width: `${d.pct}%` }} />
                  </div>
                  <div className="mt-1 text-[11px] text-slate-400 font-mono">{d.correct}/{d.total} · {d.pct}%</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[10px] text-slate-400 leading-snug">Band scores are practice estimates — not official PMI scoring.</p>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <h3 className="text-sm font-semibold text-slate-800 mb-4">Trend on Exam {data.exam_number}</h3>
            {trendData.length > 1 ? (
              <div className="h-36" data-testid="trend-sparkline">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <ChartTooltip formatter={(v) => [`${v}%`, "Score"]} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Line type="monotone" dataKey="pct" stroke="#1d4ed8" strokeWidth={2} dot={{ r: 3, fill: "#1d4ed8" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-slate-400">First attempt on this exam — take it again to see your trend.</p>
            )}
            <div className="mt-3 space-y-1">
              {trend.slice(-4).reverse().map((t) => (
                <div key={t.id} className="flex justify-between text-xs text-slate-500">
                  <span>{new Date(t.completed_at).toLocaleDateString()} · {t.mode}</span>
                  <span className="font-mono font-medium text-slate-700">{t.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 mb-5">
          <h2 className="text-xl font-semibold tracking-tight text-slate-900 mr-auto">Question review</h2>
          <select
            data-testid="report-filter-select"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">All questions</option>
            <option value="wrong">Wrong only</option>
            <option value="marked">Marked only</option>
          </select>
          <select
            data-testid="report-domain-select"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="text-sm border border-slate-300 rounded-lg px-3 py-2 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600"
          >
            <option value="all">All domains</option>
            {domains.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>

        <div className="space-y-3" data-testid="report-question-list">
          {filtered.length === 0 && <p className="text-sm text-slate-400 py-8">No questions match this filter.</p>}
          {filtered.map((q) => {
            const open = expanded[q.question_number];
            return (
              <div key={q.question_number}
                className={`bg-white rounded-xl border shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden border-l-4 ${q.is_correct ? "border-l-emerald-400 border-slate-200" : "border-l-rose-400 border-slate-200"}`}>
                <button
                  data-testid={`report-question-${q.question_number}`}
                  onClick={() => setExpanded((p) => ({ ...p, [q.question_number]: !open }))}
                  className="w-full flex items-start gap-3 p-4 text-left hover:bg-slate-50 transition-colors"
                >
                  {q.is_correct
                    ? <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-xs text-slate-400 mb-1">
                      <span className="font-mono font-medium text-slate-600">Q{q.question_number}</span>
                      <span>{q.domain}</span>
                      {q.marked && <Flag className="w-3 h-3 text-amber-500 fill-amber-400" />}
                    </div>
                    <p className={`text-sm text-slate-800 ${open ? "" : "line-clamp-2"}`}>{q.stem}</p>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-4 pb-5 pl-12">
                    {q.image_url && <img src={resolveAsset(q.image_url)} alt="Exhibit" className="mb-4 max-w-md w-full rounded-lg border border-slate-200" />}
                    <div className="space-y-1.5">
                      {q.options.map((opt, i) => (
                        <div key={i} className={`text-sm px-3 py-2 rounded-md border flex items-center gap-2 ${
                          i === q.correct_index ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                          : i === q.user_answer ? "border-rose-300 bg-rose-50 text-rose-900"
                          : "border-slate-100 text-slate-600"}`}>
                          <span className="font-mono font-semibold text-xs">{"ABCD"[i]}</span>
                          <span>{opt}</span>
                          {i === q.user_answer && <span className="ml-auto text-[10px] uppercase tracking-wide font-semibold shrink-0">Your answer</span>}
                          {i === q.correct_index && i !== q.user_answer && <span className="ml-auto text-[10px] uppercase tracking-wide font-semibold shrink-0">Correct</span>}
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed bg-slate-50 rounded-lg p-3">{q.explanation}</p>
                    <div className="mt-2">
                      <FeedbackLink sessionId={sessionId} questionNumber={q.question_number} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>
    </div>
  );
}
